import { useTypeWire } from "@typewirets/react";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteAccounts } from "@/hooks/api/use-accounts";
import { useDeepResearchData } from "@/hooks/api/use-deep-research";
import { useInfiniteOpportunities } from "@/hooks/api/use-opportunities";
import { DeepResearchReportsAPIWire } from "@/lib/api/deep-research-reports.api.wire";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";

type EntityType = "opportunity" | "account";
type ReportMode = "editor" | "viewer";

interface UseEntityReportDataOptions {
  entityType: EntityType;
  mode: ReportMode;
  report: DeepResearchReportMetadata;
  queryParams?: Record<string, string | undefined>; // Standard entity query params
  enabled?: boolean; // Optional: allow callers to delay DRR fetch until ready
}

/**
 * Data source adapter hook that switches between live entity data and DRR data
 * based on the report mode and status.
 *
 * This replaces the complex custom table implementation with a simple adapter
 * that makes DRR data look like regular entity data.
 */
export function useEntityReportData({
  entityType,
  mode,
  report,
  queryParams = {},
  enabled = true,
}: UseEntityReportDataOptions) {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  // Determine which data source to use
  const shouldUseDRRData =
    mode === "viewer" ||
    (mode === "editor" &&
      (report.status === "queued" || report.status === "paused"));

  const shouldUseLiveData =
    mode === "editor" &&
    (report.status === "draft" || report.status === "pending_review");

  // For DRR data, we need to pass the report ID and transform the response
  // to match the infinite query format that the main tables expect
  const drrQueryParams = useMemo(() => {
    if (!shouldUseDRRData) return undefined;

    // Convert standard entity query params to DRR format
    const params: Record<string, unknown> = {};

    // Parse the string params
    if (queryParams.limit) params.limit = parseInt(queryParams.limit, 10);
    if (queryParams.offset) params.offset = parseInt(queryParams.offset, 10);
    if (queryParams.sort_by) {
      params.sort_by = queryParams.sort_by;
    }
    if (queryParams.sort_direction) {
      params.sort_direction = queryParams.sort_direction;
    }
    if (queryParams.search) {
      params.search = queryParams.search;
    }
    if (queryParams.filters) {
      params.filters = queryParams.filters; // Already stringified
    }
    if (queryParams.extra_fields) {
      params.extra_fields = queryParams.extra_fields;
    }

    return params;
  }, [shouldUseDRRData, queryParams]);

  const exportAllData = async (): Promise<Record<string, unknown>[]> => {
    if (!shouldUseDRRData || !report.id) {
      return [];
    }

    const allData: Record<string, unknown>[] = [];
    let offset = 0;
    let hasMoreData = true;
    const pageSize = 1000; // A reasonable page size for export fetching

    while (hasMoreData) {
      const pageData = await drrApi.fetchReportData(report.id, {
        ...(drrQueryParams as Record<string, unknown>),
        limit: pageSize,
        offset,
      });

      if (pageData.results) {
        allData.push(...pageData.results);
      }

      const pageCount = pageData.results?.length ?? 0;
      if (pageCount === 0) {
        hasMoreData = false;
      } else {
        hasMoreData = allData.length < pageData.total;
        offset += pageCount;
      }
    }

    return allData;
  };

  // Fetch DRR data if needed
  // Track rolling offset for pagination
  const [offset, setOffset] = useState<number>(
    typeof (drrQueryParams as Record<string, unknown> | undefined)?.offset ===
      "number"
      ? ((drrQueryParams as Record<string, unknown>).offset as number)
      : 0,
  );

  // Reset offset when the core query identity changes (excluding offset)
  const drrIdentityKey = useMemo(() => {
    const qp = (drrQueryParams || {}) as Record<string, unknown>;
    const base = {
      limit: qp.limit,
      sort_by: qp.sort_by,
      sort_direction: qp.sort_direction,
      search: qp.search,
      filters: qp.filters,
      extra_fields: qp.extra_fields,
    };
    return JSON.stringify(base);
  }, [drrQueryParams]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: keeping the original code that is working
  useEffect(() => {
    setOffset(0);
  }, [drrIdentityKey]);

  const pageLimit: number = useMemo(() => {
    const qp = (drrQueryParams || {}) as Record<string, unknown>;
    return typeof qp.limit === "number" ? (qp.limit as number) : 200;
  }, [drrQueryParams]);

  const {
    data: drrData,
    isLoading: isDRRLoading,
    isError: isDRRError,
    error: drrError,
  } = useDeepResearchData(
    shouldUseDRRData && enabled ? report.id : undefined,
    shouldUseDRRData && enabled
      ? ({
          ...(drrQueryParams as Record<string, unknown>),
          limit: pageLimit,
          offset,
        } as Record<string, unknown>)
      : undefined,
  );

  // Fetch live entity data based on entity type
  const opportunityQuery = useInfiniteOpportunities(
    shouldUseLiveData && entityType === "opportunity" ? queryParams : undefined,
  );

  const accountQuery = useInfiniteAccounts(
    shouldUseLiveData && entityType === "account" ? queryParams : undefined,
  );

  // Transform DRR data to match infinite query format
  // Accumulate pages locally to simulate InfiniteData
  const [pages, setPages] = useState<
    {
      results: Record<string, unknown>[];
      total: number;
      limit: number;
      offset: number;
    }[]
  >([]);

  // Reset pages when identity changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: keeping the original code that is working
  useEffect(() => {
    setPages([]);
  }, [drrIdentityKey]);

  // Append current page when it loads
  useEffect(() => {
    if (!shouldUseDRRData || !drrData) return;
    setPages((prev) => {
      const exists = prev.some((p) => p.offset === drrData.offset);
      if (exists) return prev;
      const next = [...prev, drrData].sort((a, b) => a.offset - b.offset);
      return next;
    });
  }, [shouldUseDRRData, drrData]);

  const totalFetched = useMemo(
    () => pages.reduce((sum, p) => sum + (p.results?.length || 0), 0),
    [pages],
  );

  const totalAvailable = pages[0]?.total ?? drrData?.total ?? 0;

  const hasMorePages = totalFetched < totalAvailable;

  const isFetchingNextPage = isDRRLoading && pages.length > 0;

  const transformedDRRData = useMemo(() => {
    if (!shouldUseDRRData) return null;
    return {
      pages: pages.map((p) => ({
        results: (p.results || []).map((row: Record<string, unknown>) => ({
          ...row,
        })),
        total: p.total || 0,
        limit: p.limit || pageLimit,
        offset: p.offset || 0,
      })),
      pageParams: pages.map((p) => p.offset),
    };
  }, [shouldUseDRRData, pages, pageLimit]);

  // Return the appropriate data source based on mode and entity type
  if (shouldUseDRRData) {
    return {
      data: transformedDRRData,
      isLoading: isDRRLoading && pages.length === 0,
      fetchNextPage: () => {
        if (!hasMorePages || isDRRLoading) return;
        setOffset(totalFetched);
      },
      hasNextPage: hasMorePages,
      isFetchingNextPage,
      isError: isDRRError ?? false,
      error: drrError ?? null,
      exportAllData, // Add the export function to the return value
    };
  }

  if (entityType === "opportunity") {
    return opportunityQuery;
  }

  if (entityType === "account") {
    return accountQuery;
  }

  // This should never be reached given the EntityType union
  throw new Error(`Entity type ${entityType} not supported`);
}
